<template src="./templateTableauStats.vue" />

<script>
import LabelInput from "@/components/generics/LabelInput.vue";
import Tri from "@/components/stats/tableau/Tri.vue";

import Multiselect from "vue-multiselect";
import moment from "moment";

import MixinModalEdition from "@/mixins/mixinModalEdition";
import MixinMultiselect from "@/mixins/mixinMultiselect";

import PDFViewer from "@/components/stats/tableau/PDFViewer.vue";
import CollapsibleModalHeader from "@/components/generics/CollapsibleModalHeader.vue";

import Api from "@/modules/axios";
import { displaySuccess, displayBddError } from "@/modules/utils";

import config from "@/config";

export default {
  name: "TableauStats",
  components: {
    LabelInput,
    Multiselect,
    Tri,
    PDFViewer,
    CollapsibleModalHeader,
  },
  mixins: [MixinModalEdition, MixinMultiselect],
  props: {
    /**
     * Définit les infos relatives à la statistique dans la modale.
     */
    statInModal: {
      type: Object,
      required: true,
    },
    /**
     * Définit le type de la statistique.
     * @values libraire, ambassade, global, stock
     */
    type: {
      type: String,
      required: true,
    },
    /**
     * Définit le sous-type de la statistique.
     * @values vente par livre, vente par pays, vente par libraire,
     *         commande par livre, commande par pays, commande par ambassade,
     *         diffusion des livres, récap. année fiscale,
     *         plv, ouvrage
     */
    sousType: {
      type: String,
      required: true,
    },
    /**
     * Définit si le champ est éditable ou non.
     */
    disabled: Boolean,
    /**
     * Définit la statistique comme étant en cours de création.
     * Si false, elle est en cours de modification.
     */
    newStat: Boolean,
    /**
     * Définit le namespace du tableau en question des listes des statistiques en arrière-plan.
     */
    genericTableNamespace: {
      type: String,
      required: true,
    },
  },
  emits: [
    /**
     * Déclenché lorsqu'une nouvelle statistique est créée.
     * @param {Boolean}
     */
    "entity-created",
    /**
     * Déclenché lorsqu'on enregistre les infos de la statistique.
     * @param {Object} stat Infos de la statistique.
     */
    "update-stat-in-modal",
  ],
  data() {
    return {
      stat: {
        filtres: {},
        tris: [],
        date_start: new Date(),
      },

      auteurs: [],
      series: [],
      themes: [],
      sousThemes: [],
      tabPays: [],
      libraires: [],
      ambassades: [],
      categories: [],
      fournisseurs: [],
      ouvrages: [],
      tenants: [],
      lignesTri: [{}],
      supports: [],

      optionMultiselect: {
        noResult: "Aucun résultat.",
        emptyMessage: "Tapez 2 caractères min.",
        emptyMessageSousTheme: "Aucun sous-thème dans ce thème.",
      },

      isGenerating: false,
      displayPdf: false,
    };
  },
  computed: {
    champsTri() {
      let champs = [];

      if (this.type !== "stock") {
        champs.push(
          { key: "quantite__sum", value: "Quantité" },
        );
      }

      if (this.sousType !== "PLV") {
        champs = [
          ...champs,
          { key: "titre_francais", value: "Titre français" },
          { key: "titre_us", value: "Titre anglais" },
          { key: "auteur_principal__nom", value: "Auteur" },
          { key: "theme__nom", value: "Thème" },
          { key: "sous_theme__nom", value: "Sous-thème" },
          { key: "catalogue_general", value: "Général" },
          { key: "catalogue_jeunesse", value: "Jeunesse" },
          { key: "categorie_general__nom", value: "Catégorie catalogue général" },
          { key: "categorie_jeunesse__nom", value: "Catégorie catalogue jeunesse" },
          { key: "serie__nom", value: "Série" },
          { key: "album", value: "Album" },
          { key: "disponible", value: "Disponible" },
          { key: "indisponible", value: "Indisponible" },
        ];

        if (this.type === "libraire") {
          champs = [
            ...champs.slice(0, 5),
            { key: "libraire", value: "Libraire (compte)" },
            { key: "pays", value: "Pays" },
            ...champs.slice(5),
          ];
        } else if (this.type === "ambassade") {
          champs = [
            ...champs.slice(0, 5),
            { key: "ambassade", value: "Ambassade (compte)" },
            { key: "pays", value: "Pays" },
            ...champs.slice(5),
          ];
        } else if (this.type === "global") {
          champs = [
            ...champs.slice(0, 5),
            // { key: "pays", value: "Pays" },
            // { key: "libraire", value: "Libraire (compte)" },
            // { key: "ambassade", value: "Ambassade (compte)" },
            ...champs.slice(5),
          ];
        } else if (this.sousType === "ouvrage") { // Stock
          champs = [
            ...champs.slice(0, 5),
            { key: "stock_previsionnel", value: "Stock Dispo." },
            { key: "stock__stock_reel", value: "Stock" },
            ...champs.slice(5),
          ];
        }
      } else {
        // Plv
        champs = [
          ...champs,
          { key: "libelle", value: "Libellé" },
          { key: "ean", value: "EAN" },
          { key: "fournisseur__nom", value: "Fournisseur" },
          { key: "hs_code", value: "HS code" },
          { key: "code", value: "Code" },
          { key: "stock_reel", value: "Stock" },
        ];
      }
      return champs.sort((a, b) => a.value.localeCompare(b.value));
    },
    categoriesGeneral() {
      return this.categories.filter((categ) => categ.catalogue === "general");
    },
    categoriesJeunesse() {
      return this.categories.filter((categ) => categ.catalogue === "jeunesse");
    },
  },
  watch: {
    "stat.filtres.theme": function watchTheme(a, b) {
      if ((a && b && a.id !== b.id) || !a || !b) {
        this.stat.filtres.sous_theme = null;
        this.sousThemes = [];
        if (this.stat.filtres.theme && this.stat.filtres.theme.id) {
          Api()
            .get(`/sous_theme/?theme_parent=${this.stat.filtres.theme.id}`)
            .then((res) => {
              this.sousThemes = res.data.results;
            });
        }
      }
    },
    "stat.filtres.catalogue_general": function watchCatGeneral() {
      if (!this.stat.filtres.catalogue_general) {
        this.stat.filtres.categorie_general = null;
      }
    },
    "stat.filtres.catalogue_jeunesse": function watchCatJeunesse() {
      if (!this.stat.filtres.catalogue_jeunesse) {
        this.stat.filtres.categorie_jeunesse = null;
        this.stat.filtres.serie = null;
      }
    },
  },
  methods: {
    /**
     * Initialise la statistique dans la modale.
     * Utilise le verbose.
     * @public
     */
    initForm() {
      this.stat = { ...this.statInModal };
      this.stat.filtres = { ...this.statInModal.filtres };

      // Transformer le tableau de strings en tableau d'objets pour le tri et le sens du tri
      // Ex : ["-quantite"] => [{...}]
      this.lignesTri = [];
      if (this.statInModal.tris) {
        this.statInModal.tris.forEach((item) => {
          const sensOrdering = item.charAt(0) === "-"
            ? { key: "-", value: "Décroissant" }
            : { key: "+", value: "Croissant" };

          const obj = {
            ordering: this.champsTri.find((champ) => champ.key === item.replace("-", "")),
            sens_ordering: sensOrdering,
          };

          this.lignesTri.push(obj);
        });
      }

      // Pour stocker le sous-thème avant écrasement par le forEach (watch() du thème)
      const tempSousTheme = this.stat.filtres.sous_theme;

      // Verbose des FK
      const champsFK = [
        { champ: "auteur_principal", options: "auteurs", url: "auteur" },
        { champ: "serie", options: "series" },
        { champ: "support", options: "supports" },
        { champ: "theme", options: "themes" },
        { champ: "pays", options: "tabPays" },
        { champ: "libraire", options: "libraires" },
        { champ: "ambassade", options: "ambassades" },
        { champ: "categorie", options: "categories" },
        { champ: "ouvrage", options: "ouvrages" },
        { champ: "tenant", options: "tenants" },
      ];

      const promises = [];

      champsFK.forEach((el) => {
        let promise = null;

        if (this.stat.filtres[el.champ]) {
          const url = el.url || el.champ;

          promise = Api()
            .get(`/${url}/${this.stat.filtres[el.champ]}/`)
            .then((res) => {
              this.$set(this.stat.filtres, el.champ, res.data);
              this.$set(this, el.options, [res.data]);
            });
        }
        promises.push(promise);
      });

      Promise.all(promises).then(() => {
        // Pour éviter que sous-thème ne soit null
        if (!this.newStat && tempSousTheme != null) {
          Api()
            .get(`/sous_theme/${tempSousTheme}/`)
            .then((res) => {
              this.stat.filtres.sous_theme = res.data;
            });
        }

        // Afficher tous les sous-thèmes
        if (this.stat.filtres.theme && this.stat.filtres.theme.id) {
          Api()
            .get(`/sous_theme/?theme_parent=${this.stat.filtres.theme.id}`)
            .then((res) => {
              this.sousThemes = res.data.results;
            });
        }
      });

      // Afficher tous les thèmes
      Api()
        .get("/theme/")
        .then((res) => {
          this.themes = res.data.results;
        });

      // Afficher toutes les catégories
      Api()
        .get("/categorie/")
        .then((res) => {
          this.categories = res.data.results;
        });

      // Verbose de la catégorie "général"
      if (this.stat.filtres.categorie_general) {
        Api()
          .get(`/categorie/${this.stat.filtres.categorie_general}`)
          .then((res) => {
            this.stat.filtres.categorie_general = res.data;
          });
      }

      // Verbose de la catégorie "jeunesse"
      if (this.stat.filtres.categorie_jeunesse) {
        Api()
          .get(`/categorie/${this.stat.filtres.categorie_jeunesse}`)
          .then((res) => {
            this.stat.filtres.categorie_jeunesse = res.data;
          });
      }

      // Verbose du fournisseur
      if (this.stat.filtres && this.stat.filtres.fournisseur) {
        Api()
          .get(`/plv_fournisseur/${this.stat.filtres.fournisseur}/`)
          .then((res) => {
            this.stat.filtres.fournisseur = res.data;
          });
      }

      // Radio disponibilité
      if (this.stat.filtres.disponible) {
        this.stat.filtres.disponibilite = "disponible";
      } else if (this.stat.filtres.indisponible) {
        this.stat.filtres.disponibilite = "indisponible";
      } else {
        this.stat.filtres.disponibilite = "both";
      }
    },
    /**
     * Effectue une recherche dans le multiselect à partir de 2 caractères tapés.
     * @public
     * @param {String} searchQuery Texte recherché dans le multiselect.
     * @param {String} url Nom de la table pour l'url.
     * @param {String} field Nom du champ.
     */
    // On laisse cette fonction ici car elle est légèrement différente de celle de la mixin
    searchMinCaracteresOuvrages(searchQuery, url, field) {
      // Si au moins 2 caractères de tapés dans l'input, afficher les résultats correspondants
      if (searchQuery.length >= 2) {
        const alreadyHaveParams = url.indexOf("?") > -1;
        Api()
          .get(
            `/${url.toLowerCase()}${
              alreadyHaveParams ? "&" : "?"
            }search_titres=${searchQuery}&tenant=${this.$store.getters.typeUser.id}`,
          )
          .then((res) => {
            this[field] = res.data.results;
            this.optionMultiselect.noResult = "Aucun résultat.";
          });
      } else {
        this.optionMultiselect.noResult = this.optionMultiselect.emptyMessage;
      }
    },
    /**
     * Enregistre la statistique.
     * @public
     */
    saveStat() {
      const sentData = { ...this.stat };

      sentData.filtres = { ...this.stat.filtres };

      sentData.tris = [];
      this.lignesTri.forEach((item) => {
        if (item.sens_ordering && item.ordering) {
          sentData.tris.push((item.sens_ordering.key === "+" ? "" : "-") + item.ordering.key);
        }
      });

      // Si une seule date est renseignée
      if (
        this.type.toLowerCase() !== "stock"
        && this.sousType.toLowerCase() !== "récap. année fiscale"
        && (
          (sentData.date_start != null && sentData.date_end == null)
          || (sentData.date_start == null && sentData.date_end != null)
        )
      ) {
        this.$notify({
          type: "error",
          title: "Erreur",
          text: "Veuillez renseigner une date initiale et une date finale.",
        });
      } else if (
        // Si la date initiale est plus récente que la date finale
        // date_start - date_end < 0 : date_start avant date_end
        // date_start - date_end > 0 : date_start après date_end
        // date_start - date_end = 0 : dates identiques
        moment(sentData.date_start).diff(moment(sentData.date_end)) > 0
      ) {
        this.$notify({
          type: "error",
          title: "Erreur",
          text: "La date finale ne peut être antérieure à la date initiale.",
        });
      } else {
        const typesUser = ["user_created", "user_updated", "user_archived"];
        typesUser.forEach((type) => {
          if (sentData[type]) {
            sentData[type] = sentData[type].id;
          }
        });

        Object.keys(sentData.filtres).forEach((champ) => {
          if (sentData.filtres[champ] != null) {
            if (Array.isArray(sentData.filtres[champ])) {
              sentData.filtres[champ] = sentData.filtres[champ].map(
                (item) => item.id,
              );
            } else if (typeof sentData.filtres[champ] === "object") {
              sentData.filtres[champ] = sentData.filtres[champ].id;
            } else {
              // Si champ normal ne rien faire
            }
          }
        });

        // Définir le type et le sous-type
        sentData.type = this.type;
        sentData.sous_type = this.sousType;

        // Ne pas envoyer les filtres des multiselects s'ils sont null
        const champsFK = [
          "auteur_principal",
          "serie",
          "theme",
          "sous_theme",
          "pays",
          "libraire",
          "ambassade",
          "categorie",
        ];

        champsFK.forEach((champ) => {
          if (
            sentData.filtres[champ] == null
            || sentData.filtres[champ] === ""
          ) {
            delete sentData.filtres[champ];
          }
        });

        // Définir la valeur des boutons radio
        if (sentData.filtres.disponibilite === "disponible") {
          sentData.filtres.disponible = true;
          delete sentData.filtres.indisponible;
        } else if (sentData.filtres.disponibilite === "indisponible") {
          sentData.filtres.indisponible = true;
          delete sentData.filtres.disponible;
        } else {
          delete sentData.filtres.disponible;
          delete sentData.filtres.indisponible;
        }
        if (sentData.filtres.auteurs && sentData.filtres.auteurs.length === 0) {
          sentData.filtres.auteurs = null;
        }
        // On vide le champ car on n'a pas besoin de mettre les 2 à true
        // => le back met les 2 à true par défaut
        delete sentData.filtres.disponibilite;

        //  On vide le fichier car on ne veut pas l'enregistrer, il est généré en back
        delete sentData.fichier;

        let promise = null;

        if (this.newStat) {
          promise = Api().post("/statistique/", sentData);
        } else {
          promise = Api().patch(`/statistique/${sentData.id}/`, sentData);
        }

        promise
          .then((res) => {
            this.$store.dispatch(`${this.genericTableNamespace}/fetchData`);
            if (this.newStat) {
              // Afficher l'entité créée dans la modale
              this.$emit("entity-created", true);
              this.stat.id = res.data.id;
            }
            displaySuccess();
            // Garder les dernières modififications sauvegardées au changement d'onglet
            this.$emit("update-stat-in-modal", res.data);
          })
          .catch(displayBddError);
      }
    },
    /**
     * Exporte les données de la statistique.
     * @public
     */
    exportSimple() {
      this.$notify({
        title: "Création du fichier de statistique...",
        text: "Veuillez patienter",
        duration: config.durationNotifications,
      });

      this.isGenerating = true;

      // S'il n'y a que "disponibilite" et "ordering" = pas de filtres
      if (
        !Object.keys(this.stat.filtres)
          .some(
            (key) => (["ordering"].indexOf(key) === -1),
          )
      ) {
        this.$notify({
          type: "warn",
          title: "ATTENTION",
          text: "Sans filtres, la génération peut prendre jusqu'à 3 minutes.",
          duration: config.durationNotifications,
        });
      }

      Api({ timeout: 60 * 60 * 1000 })
        .post(`/statistique/${this.stat.id}/generate/`)
        .then((res) => {
          this.$set(this.stat, "fichier", res.data.fichier);
          this.isGenerating = false;

          displaySuccess();
          this.openExport();
        })
        .catch(displayBddError);
    },
    /**
     * Ouvre le rendu de l'export dans un nouvel onglet.
     * @public
     */
    openExport() {
      window.open(this.stat.fichier, "_blank");
    },
    ajouterTri() {
    /**
     * Ajouter un tri sur un champ.
     * @public
     */
      this.lignesTri.push({});
    },
    /**
     * Supprime un tri.
     * @public
     * @param {Number} index Index de la ligne du tri.
     */
    deleteRow(index) {
      if (this.lignesTri.length === 1) {
        this.$set(this.lignesTri, index, {});
      } else {
        this.lignesTri.splice(index, 1);
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
